import { RefObject, useEffect, useState } from 'react';
import { useConsent, YOUTUBE_REQUIRED_GROUPS } from 'application/adapters/context/ConsentContext';
import { YOUTUBE_CONSENT_SRC_ATTRIBUTE } from 'helpers/youtubeEmbed';

export interface YouTubeConsentPlaceholderTarget {
	element: HTMLElement;
	// True when the iframe was absolutely positioned (e.g. a padding-top responsive wrapper),
	// so the placeholder should fill that area instead of keeping its own 16:9 ratio
	fillsContainer: boolean;
}

/**
 * Gates the YouTube iframes in CMS HTML that has been through deferYouTubeIframes.
 * With consent each iframe gets its src back. Without it the iframe is hidden and an empty
 * element is inserted before it, for the caller to render a consent placeholder into.
 *
 * @param containerRef - Element holding the CMS HTML
 * @param content - The CMS HTML, so the iframes are processed again when it changes
 * @returns Targets to render a consent placeholder into, empty when consent is given
 */
export const useYouTubeIframeConsent = (containerRef: RefObject<HTMLElement | null>, content?: string): YouTubeConsentPlaceholderTarget[] => {
	const hasYouTubeConsent = useConsent(YOUTUBE_REQUIRED_GROUPS);
	const [placeholderTargets, setPlaceholderTargets] = useState<YouTubeConsentPlaceholderTarget[]>([]);

	useEffect(() => {
		const iframes = Array.from(containerRef.current?.querySelectorAll<HTMLIFrameElement>(`iframe[${YOUTUBE_CONSENT_SRC_ATTRIBUTE}]`) ?? []);
		if (!iframes.length) return;

		if (hasYouTubeConsent) {
			iframes.forEach((iframe) => {
				iframe.hidden = false;
				iframe.setAttribute('src', iframe.getAttribute(YOUTUBE_CONSENT_SRC_ATTRIBUTE));
			});
			return;
		}

		// The surrounding HTML comes from the CMS, so targets are made with createElement and never innerHTML
		const targets = iframes.map((iframe) => {
			// Computed style also catches positioning from a CSS class, not just an inline style
			const fillsContainer = window.getComputedStyle(iframe).position === 'absolute';

			// Removing src also unloads the video if consent is withdrawn
			iframe.removeAttribute('src');
			iframe.hidden = true;

			const element = document.createElement('div');
			if (fillsContainer) {
				// Covers the same area as the iframe, so a padding-top wrapper doesn't leave an empty gap above the placeholder
				element.style.position = 'absolute';
				element.style.inset = '0';
			} else if (/^\d+$/.test(iframe.width)) {
				element.style.maxWidth = `${iframe.width}px`;
			}
			iframe.before(element);
			return { element, fillsContainer };
		});
		setPlaceholderTargets(targets);

		return () => {
			targets.forEach(({ element }) => element.remove());
			setPlaceholderTargets([]);
		};
	}, [containerRef, content, hasYouTubeConsent]);

	return placeholderTargets;
};
